import { REGULAR_EXPRESSION, TAGS_MAX_COUNT } from "../app.constants";

/** Labels the tag list has a tag for (in the list's spelling) and labels it has none for (TagItems.match). */
export interface TagMatch {
  tags: string[];
  unmatchedTags: string[];
}

/**
 * The tenant tag list rules of the Tags settings (SettingsTagsEditDialogComponent): at most TAGS_MAX_COUNT labels of 1 to
 * LABEL_MAX_LENGTH characters from the allowed character set. The Merry import adds labels under the same rules, so the
 * settings dialog can always save the list it produced. TagItems.cs in the data management API is the counterpart.
 */
export class TagItems {
  static readonly LABEL_MAX_LENGTH = 30;

  private static readonly LABEL_PATTERN = new RegExp(
    [
      "^[",
      REGULAR_EXPRESSION.HALF_ALPHANUMERIC_PATTERN,
      REGULAR_EXPRESSION.KANJI_PATTERN,
      REGULAR_EXPRESSION.SYMBOL_PATTERN,
      REGULAR_EXPRESSION.OTHER_SYMBOL_PATTERN,
      "]+$"
    ].join(""),
    "i"
  );

  /** Whether the Tags settings accept the label. */
  static isValidLabel(label: string | null | undefined): boolean {
    const text = label?.trim() ?? "";
    return text.length > 0 && text.length <= TagItems.LABEL_MAX_LENGTH && TagItems.LABEL_PATTERN.test(text);
  }

  /** The list's tag for a label: the same text, else the same text up to case and surrounding spaces. */
  static find(items: readonly string[], label: string): string | undefined {
    if (items.includes(label)) {
      return label;
    }
    const normalized = TagItems.normalize(label);
    return items.find((item) => TagItems.normalize(item) === normalized);
  }

  /** The list with the valid labels it has no tag for appended, in order, while it is under TAGS_MAX_COUNT. */
  static add(items: readonly string[], labels: readonly string[]): string[] {
    const result = [...items];
    for (const label of labels) {
      const text = label.trim();
      if (result.length < TAGS_MAX_COUNT && TagItems.isValidLabel(text) && !TagItems.find(result, text)) {
        result.push(text);
      }
    }
    return result;
  }

  /** The list's tags for the labels (each once, in label order) and the labels it has no tag for. */
  static match(items: readonly string[], labels: readonly string[]): TagMatch {
    const result: TagMatch = { tags: [], unmatchedTags: [] };
    for (const label of labels) {
      const text = label.trim();
      const tag = TagItems.find(items, text);
      if (tag) {
        if (!result.tags.includes(tag)) {
          result.tags.push(tag);
        }
      } else if (text && !TagItems.find(result.unmatchedTags, text)) {
        result.unmatchedTags.push(text);
      }
    }
    return result;
  }

  private static normalize(text: string): string {
    return text.trim().toLowerCase();
  }
}
